<template>
  <div v-if="pcm" class="pcm-auditor">
    <section class="pcm-auditor-bloco">
      <div class="pcm-auditor-titulo">
        <h2>Aprovação Vigente</h2>
        <span class="pcm-auditor-badge" :class="pcm.aprovada ? 'ok' : 'no'">
          {{ badgeLabel }}
        </span>
      </div>

      <div class="pcm-auditor-meta">
        <div class="pcm-auditor-campo">
          <label>Status do Projeto</label>
          <p>{{ statusProjeto || "—" }}</p>
        </div>
        <div class="pcm-auditor-campo">
          <label>Data Prevista de Conclusão</label>
          <p>{{ fmtData(pcm.dt_prevista_conclusao) }}</p>
        </div>
        <div class="pcm-auditor-campo">
          <label>Gerente Responsável</label>
          <p>{{ pcm.gerente_nome || "—" }}</p>
        </div>
        <div class="pcm-auditor-campo">
          <label>Custo Previsto Total</label>
          <p>{{ fmtMoeda(custoTotal) }}</p>
        </div>
        <div class="pcm-auditor-campo">
          <label>Solicitante</label>
          <p>{{ pcm.solicitante_nome || "—" }}</p>
        </div>
        <div class="pcm-auditor-campo">
          <label>Data de Aprovação</label>
          <p>{{ fmtData(pcm.dt_aprovacao) }}</p>
        </div>
        <div v-if="pcm.nivel" class="pcm-auditor-campo">
          <label>Nível</label>
          <p>{{ pcm.nivel }}</p>
        </div>
        <div v-if="pcm.codigo" class="pcm-auditor-campo">
          <label>Código PCM</label>
          <p class="mono">{{ pcm.codigo }}</p>
        </div>
      </div>

      <div class="pcm-auditor-secao">
        <label>Entregas</label>
        <div class="pcm-auditor-table-wrap">
          <table class="pcm-auditor-table">
            <thead>
              <tr>
                <th class="num">#</th>
                <th>Entrega</th>
                <th>Evidências</th>
                <th>Data prevista</th>
                <th>Data de conclusão</th>
                <th>Custo Previsto</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="!resultados.length">
                <td colspan="7" class="vazio">Nenhuma entrega cadastrada.</td>
              </tr>
              <tr v-for="(res, idx) in resultados" :key="res.id || idx">
                <td class="num">{{ idx + 1 }}.</td>
                <td class="txt">
                  <strong v-if="res.nome">{{ res.nome }}</strong>
                  <span>{{ res.descricao || "—" }}</span>
                </td>
                <td class="txt">{{ res.evidencias || "—" }}</td>
                <td>{{ fmtData(res.dt_prevista_conclusao) }}</td>
                <td>{{ fmtData(res.dt_conclusao) }}</td>
                <td>
                  <span v-if="res.sem_custo" class="muted">Sem custo</span>
                  <span v-else>{{ fmtMoeda(res.custo) }}</span>
                </td>
                <td>
                  <span class="pcm-status" :class="statusClass(res.status)">{{ statusLabel(res.status) }}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="pcm-auditor-secao">
        <label>Comitê de Aprovação</label>
        <div v-if="comite.length" class="pcm-auditor-tags">
          <span v-for="m in comite" :key="m.id || m.name" class="pcm-tag">{{ m.name }}</span>
        </div>
        <p v-else class="muted">Nenhum membro no comitê.</p>
      </div>

      <div class="pcm-auditor-secao">
        <label>Anexos</label>
        <ul v-if="anexos.length" class="pcm-auditor-anexos">
          <li v-for="a in anexos" :key="a.id">
            <a v-if="a.caminho" :href="storageUrl + a.caminho" target="_blank">{{ a.nome_original }}</a>
            <span v-else>{{ a.nome_original }}</span>
          </li>
        </ul>
        <p v-else class="muted"><i>Nenhum anexo</i></p>
      </div>

      <div v-if="pcm.parecer_responsavel" class="pcm-auditor-secao">
        <label>Parecer do Responsável</label>
        <p class="pcm-auditor-texto">{{ pcm.parecer_responsavel }}</p>
      </div>

      <div v-if="pcm.custo_justificativa" class="pcm-auditor-secao">
        <label>Justificativa do Custo</label>
        <p class="pcm-auditor-texto">{{ pcm.custo_justificativa }}</p>
      </div>

      <div v-if="pcm.aprovada && pcm.dt_aprovacao" class="pcm-auditor-aprovado-em">
        Aprovado em
        <strong>{{ fmtDataLonga(pcm.dt_aprovacao) }}</strong>
      </div>
    </section>

    <section class="pcm-auditor-bloco ata">
      <h2 class="pcm-auditor-ata-titulo">Ata de Aprovação Inicial</h2>
      <div v-if="temAta" class="pcm-auditor-ata ql-snow">
        <div class="ql-editor" v-html="pcm.ata"></div>
      </div>
      <p v-else class="muted alinha">Nenhuma ata registrada.</p>
    </section>

    <section class="pcm-auditor-bloco">
      <h2 class="pcm-auditor-ata-titulo">Pedidos de Mudança</h2>
      <div v-if="!pedidos.length" class="muted alinha">
        <span class="pcm-pedido-chip slate">Nenhum pedido de mudança solicitado</span>
      </div>
      <div v-else class="pcm-pedidos">
        <div v-for="(pedido, index) in pedidos" :key="pedido.id || index" class="pcm-pedido">
          <div class="pcm-pedido-ordem">{{ pedidos.length - index }}º</div>
          <div class="pcm-pedido-card">
            <div class="pcm-pedido-grid">
              <div class="pcm-auditor-campo">
                <label>Campo</label>
                <p>{{ labelCampoPedido(pedido) }}</p>
              </div>
              <div class="pcm-auditor-campo">
                <label>Valor Original</label>
                <p class="pre">{{ formatarValorPedido(pedido, "atual") }}</p>
              </div>
              <div class="pcm-auditor-campo">
                <label>Novo Valor</label>
                <p class="pre">{{ formatarValorPedido(pedido, "novo") }}</p>
              </div>
              <div class="pcm-auditor-campo">
                <label>Solicitado Por</label>
                <p>{{ pedido.solicitante?.name || "—" }}</p>
              </div>
              <div class="pcm-auditor-campo">
                <label>Data da Solicitação</label>
                <p>{{ fmtData(pedido.created_at) }}</p>
              </div>
              <div class="pcm-auditor-campo wide">
                <label>Justificativa</label>
                <p class="pre">{{ pedido.justificativa || "—" }}</p>
              </div>
              <div v-if="pedido.obs" class="pcm-auditor-campo wide">
                <label>{{ pedido.status === "pendente" ? "Observações do solicitante" : "Observações" }}</label>
                <p class="pre">{{ pedido.obs }}</p>
              </div>
            </div>
            <div class="pcm-pedido-status">
              <span v-if="pedido.status === 'pendente'" class="pcm-pedido-chip slate">Pendente</span>
              <span v-else-if="pedido.status === 'aprovado'" class="pcm-pedido-chip ok">
                Aprovado{{ pedido.aprovador?.name ? ` por ${pedido.aprovador.name}` : "" }}
              </span>
              <span v-else class="pcm-pedido-chip err">
                Reprovado{{ pedido.aprovador?.name ? ` por ${pedido.aprovador.name}` : "" }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import "@vueup/vue-quill/dist/vue-quill.snow.css";
import { fmtMoeda } from "@/utils/roboflexLabels";
import servicePCM from "@/services/servicePCM";

const STATUS_LABEL = {
  pendente: "Pendente",
  validado: "Validado",
  concluido: "Concluído",
  cancelado: "Cancelado",
};

export default {
  name: "ControleMudancaAuditor",
  props: {
    pcm: { type: Object, default: null },
    statusProjeto: { type: String, default: "" },
  },
  data() {
    return {
      storageUrl: process.env.VUE_APP_ROOT_STORAGE || "",
    };
  },
  computed: {
    resultados() {
      return Array.isArray(this.pcm?.resultados) ? this.pcm.resultados : [];
    },
    comite() {
      return Array.isArray(this.pcm?.comite) ? this.pcm.comite : [];
    },
    anexos() {
      return Array.isArray(this.pcm?.anexos) ? this.pcm.anexos : [];
    },
    pedidos() {
      return Array.isArray(this.pcm?.pedidos_mudanca) ? this.pcm.pedidos_mudanca : [];
    },
    custoTotal() {
      if ((this.pcm?.custo_previsto_resultados || 0) > 0) return this.pcm.custo_previsto_resultados;
      return this.pcm?.estimativa_custo || 0;
    },
    temAta() {
      const ata = (this.pcm?.ata || "").trim();
      if (!ata) return false;
      const plain = ata.replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim();
      return plain.length > 0;
    },
    badgeLabel() {
      if (this.pcm?.aprovada) return "Aprovado";
      if (this.pcm?.status) return this.pcm.status;
      return "Não aprovado";
    },
  },
  methods: {
    fmtMoeda,
    fmtData(d) {
      if (!d) return "—";
      const raw = String(d);
      const isoDay = raw.length <= 10 ? `${raw}T12:00:00` : raw;
      const date = new Date(isoDay);
      if (Number.isNaN(date.getTime())) return "—";
      return date.toLocaleDateString("pt-BR");
    },
    fmtDataLonga(d) {
      if (!d) return "—";
      const raw = String(d);
      const isoDay = raw.length <= 10 ? `${raw}T12:00:00` : raw;
      const date = new Date(isoDay);
      if (Number.isNaN(date.getTime())) return "—";
      return date.toLocaleDateString("pt-BR", {
        day: "2-digit",
        month: "long",
        year: "numeric",
      });
    },
    statusLabel(st) {
      return STATUS_LABEL[st] || st || "—";
    },
    statusClass(st) {
      if (st === "concluido" || st === "validado") return "ok";
      if (st === "cancelado") return "err";
      return "warn";
    },
    labelCampoPedido(pedido) {
      return servicePCM.labelCampoPedidoCompleto(pedido);
    },
    formatarValorPedido(pedido, qual) {
      const campo = pedido?.campo;
      const parsed = servicePCM.parseCampoPedidoResultado(campo);
      const meta = servicePCM.metaCampoPedido(campo);
      const raw = qual === "novo" ? pedido?.novo_valor : pedido?.valor_atual;

      if (parsed?.tipo === "lista" || campo === "resultados") {
        return servicePCM.resumoListaResultadosPedido(raw);
      }
      if (parsed) {
        return servicePCM.formatarValorPedidoResultado(pedido, qual);
      }
      if (campo === "objetivos_estrategicos") {
        return servicePCM.resumoListaObjetivosPedido(raw);
      }
      if (meta?.tipo === "moeda") {
        const n = Number(raw);
        if (!Number.isFinite(n)) return raw || "—";
        return fmtMoeda(n);
      }
      if (meta?.tipo === "date") {
        return this.fmtData(raw);
      }
      if (meta?.tipo === "boolean") {
        return servicePCM.formatarBoolSemCusto(raw);
      }
      if (raw == null || raw === "") return "—";
      const lista = servicePCM.parseListaPedidoValor(raw);
      if (lista.length && lista.some((item) => item && typeof item === "object")) {
        if (lista[0]?.descricao != null || lista[0]?.evidencias != null || lista[0]?.custo != null) {
          return servicePCM.resumoListaResultadosPedido(raw);
        }
        if (lista[0]?.objetivo_id != null || lista[0]?.nome) {
          return servicePCM.resumoListaObjetivosPedido(raw);
        }
      }
      if (typeof raw === "object") return servicePCM.resumoListaResultadosPedido(raw);
      return String(raw);
    },
  },
};
</script>

<style scoped>
.pcm-auditor {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}
.pcm-auditor-bloco {
  border: 1px solid #e2e8f0;
  background: #fff;
  border-radius: 0.75rem;
  padding: 1.25rem 1.5rem;
}
.pcm-auditor-titulo {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  margin-bottom: 1.25rem;
  position: relative;
}
.pcm-auditor-titulo h2 {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  color: #0f172a;
  text-align: center;
}
.pcm-auditor-badge {
  position: absolute;
  right: 0;
  border-radius: 0.25rem;
  padding: 0.2rem 0.55rem;
  font-size: 0.75rem;
  font-weight: 600;
}
.pcm-auditor-badge.ok { background: #d1fae5; color: #047857; }
.pcm-auditor-badge.no { background: #fef3c7; color: #b45309; }

.pcm-auditor-meta {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.85rem 1rem;
  margin-bottom: 1.25rem;
}
.pcm-auditor-campo label {
  display: block;
  font-size: 0.75rem;
  font-weight: 600;
  color: #64748b;
  margin-bottom: 0.2rem;
}
.pcm-auditor-campo p {
  margin: 0;
  font-size: 0.9rem;
  color: #0f172a;
  font-weight: 500;
  padding: 0.45rem 0.6rem;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 0.375rem;
  min-height: 2.25rem;
}
.pcm-auditor-campo .mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 0.8rem;
}

.pcm-auditor-secao {
  margin-top: 1rem;
}
.pcm-auditor-secao > label {
  display: block;
  font-size: 0.8rem;
  font-weight: 600;
  color: #334155;
  margin-bottom: 0.5rem;
}
.pcm-auditor-texto {
  margin: 0;
  font-size: 0.875rem;
  color: #475569;
  white-space: pre-line;
  line-height: 1.45;
  padding: 0.75rem;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
}

.pcm-auditor-table-wrap {
  overflow-x: auto;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
}
.pcm-auditor-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.8rem;
}
.pcm-auditor-table th {
  text-align: left;
  padding: 0.6rem 0.65rem;
  background: #f1f5f9;
  color: #475569;
  font-weight: 600;
  border-bottom: 1px solid #e2e8f0;
  white-space: nowrap;
}
.pcm-auditor-table td {
  padding: 0.65rem;
  border-bottom: 1px solid #f1f5f9;
  vertical-align: top;
  color: #334155;
}
.pcm-auditor-table td.num,
.pcm-auditor-table th.num {
  width: 2.5rem;
  text-align: left;
  font-weight: 600;
}
.pcm-auditor-table td.txt,
.pcm-auditor-table th:nth-child(2) {
  text-align: left;
  white-space: pre-wrap;
  min-width: 10rem;
  max-width: 18rem;
}
.pcm-auditor-table td.txt strong,
.pcm-auditor-table td.txt small,
.pcm-auditor-table td.txt span {
  display: block;
}
.pcm-auditor-table .vazio {
  text-align: center;
  color: #94a3b8;
  padding: 1.5rem;
}

.pcm-status {
  display: inline-block;
  border-radius: 999px;
  padding: 0.15rem 0.55rem;
  font-size: 0.7rem;
  font-weight: 600;
}
.pcm-status.ok { background: #d1fae5; color: #047857; }
.pcm-status.warn { background: #fef3c7; color: #b45309; }
.pcm-status.err { background: #fee2e2; color: #b91c1c; }

.pcm-auditor-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
}
.pcm-tag {
  display: inline-flex;
  align-items: center;
  padding: 0.35rem 0.75rem;
  border-radius: 999px;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  color: #1d4ed8;
  font-size: 0.8rem;
  font-weight: 500;
}

.pcm-auditor-anexos {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}
.pcm-auditor-anexos a {
  color: #2563eb;
  text-decoration: none;
  font-size: 0.875rem;
}
.pcm-auditor-anexos a:hover { text-decoration: underline; }

.pcm-auditor-aprovado-em {
  margin-top: 1rem;
  text-align: right;
  font-size: 0.875rem;
  color: #64748b;
}

.pcm-auditor-ata-titulo {
  margin: 0 0 1rem;
  font-size: 1.15rem;
  font-weight: 700;
  text-align: center;
  color: #0f172a;
}
.pcm-auditor-ata {
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  background: #fff;
  overflow: hidden;
}
.pcm-auditor-ata :deep(.ql-editor) {
  min-height: 8rem;
  padding: 1rem 1.15rem;
  color: #334155;
  line-height: 1.55;
  font-size: 0.9rem;
}
.pcm-auditor-ata :deep(.ql-editor p) { margin: 0 0 0.5rem; }
.pcm-auditor-ata :deep(.ql-editor ul),
.pcm-auditor-ata :deep(.ql-editor ol) {
  padding-left: 1.35rem;
  margin: 0.35rem 0 0.75rem;
}
.pcm-auditor-ata :deep(.ql-editor li) { margin: 0.2rem 0; }
.pcm-auditor-ata :deep(.ql-editor strong) { color: #0f172a; }

.muted { color: #94a3b8; font-size: 0.875rem; margin: 0; }
.alinha { text-align: center; padding: 1rem 0; }

.pcm-pedidos {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
.pcm-pedido {
  display: flex;
  gap: 0.75rem;
  align-items: flex-start;
}
.pcm-pedido-ordem {
  flex: 0 0 2rem;
  padding-top: 0.85rem;
  font-size: 1rem;
  font-weight: 700;
  color: #334155;
  text-align: center;
}
.pcm-pedido-card {
  flex: 1;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  background: #f8fafc;
  padding: 0.85rem 1rem 0.75rem;
}
.pcm-pedido-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.75rem 0.85rem;
}
.pcm-pedido-grid .pcm-auditor-campo.wide {
  grid-column: 1 / -1;
}
.pcm-pedido-grid .pcm-auditor-campo p.pre {
  white-space: pre-wrap;
  word-break: break-word;
  min-height: 2.25rem;
}
.pcm-pedido-status {
  display: flex;
  justify-content: flex-end;
  margin-top: 0.75rem;
}
.pcm-pedido-chip {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 0.25rem 0.7rem;
  font-size: 0.8rem;
  font-weight: 600;
}
.pcm-pedido-chip.slate { background: #e2e8f0; color: #475569; }
.pcm-pedido-chip.ok { background: #d1fae5; color: #047857; }
.pcm-pedido-chip.err { background: #fee2e2; color: #b91c1c; }

@media (max-width: 1000px) {
  .pcm-auditor-meta { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .pcm-auditor-badge { position: static; }
  .pcm-auditor-titulo { flex-wrap: wrap; }
  .pcm-pedido-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
@media (max-width: 560px) {
  .pcm-auditor-meta { grid-template-columns: 1fr; }
  .pcm-pedido-grid { grid-template-columns: 1fr; }
  .pcm-pedido { flex-direction: column; gap: 0.35rem; }
  .pcm-pedido-ordem { text-align: left; padding-top: 0; }
}

:global(.dark) .pcm-auditor-bloco,
:global(.dark) .pcm-auditor-ata {
  border-color: rgba(148, 163, 184, 0.28);
  background: #2c2c2c;
}
:global(.dark) .pcm-auditor-titulo h2,
:global(.dark) .pcm-auditor-ata-titulo,
:global(.dark) .pcm-auditor-campo p,
:global(.dark) .pcm-auditor-ata :deep(.ql-editor strong) { color: #e8eef6; }
:global(.dark) .pcm-auditor-badge.ok,
:global(.dark) .pcm-status.ok,
:global(.dark) .pcm-pedido-chip.ok { background: rgba(16, 185, 129, 0.22); color: #a7f3d0; }
:global(.dark) .pcm-auditor-badge.no,
:global(.dark) .pcm-status.warn { background: rgba(245, 158, 11, 0.22); color: #fde68a; }
:global(.dark) .pcm-status.err,
:global(.dark) .pcm-pedido-chip.err { background: rgba(239, 68, 68, 0.22); color: #fecaca; }
:global(.dark) .pcm-auditor-campo p,
:global(.dark) .pcm-auditor-texto,
:global(.dark) .pcm-pedido-card {
  background: rgba(255, 255, 255, 0.04);
  border-color: rgba(148, 163, 184, 0.28);
  color: #cbd5e1;
}
:global(.dark) .pcm-auditor-secao > label { color: #cbd5e1; }
:global(.dark) .pcm-auditor-table-wrap { border-color: rgba(148, 163, 184, 0.28); }
:global(.dark) .pcm-auditor-table th {
  background: rgba(255, 255, 255, 0.05);
  color: #94a3b8;
  border-bottom-color: rgba(148, 163, 184, 0.28);
}
:global(.dark) .pcm-auditor-table td {
  border-bottom-color: rgba(148, 163, 184, 0.18);
  color: #cbd5e1;
}
:global(.dark) .pcm-tag {
  background: rgba(59, 130, 246, 0.16);
  border-color: rgba(96, 165, 250, 0.35);
  color: #93c5fd;
}
:global(.dark) .pcm-auditor-anexos a { color: #60a5fa; }
:global(.dark) .pcm-auditor-ata :deep(.ql-editor) { color: #cbd5e1; }
:global(.dark) .pcm-pedido-ordem { color: #cbd5e1; }
:global(.dark) .pcm-pedido-chip.slate { background: rgba(148, 163, 184, 0.2); color: #cbd5e1; }
</style>
